import { inject, Injectable } from '@angular/core';
import {
  ConfigurationInstallation
} from '../../../../../models/configurators-2d/applications/fence/configuration-installation';
import { FencePanelCalculatorService } from '../../services/fence-panel-calculator.service';
import { cloneDeep } from '../../../../../services/helpers/utils';

@Injectable({
  providedIn: 'root',
})
export class MaxBoardService {
  private readonly fencePanelCalculatorService = inject(FencePanelCalculatorService);

  /**
   * Permet de connaitre le nombre de lames maximum pour la pose secondaire d'un mixage de pose
   * @param configurationInstallations
   * @param postHeight
   */
  maxBoardFromSecondaryInstallation(
    configurationInstallations: ConfigurationInstallation[],
    postHeight: number
  ): number {
    // vérifie que la configuration a bien toutes les lames de sélectionnées
    if (!configurationInstallations.some(ci => ci.boardNumber)) {
      return 1;
    }

    configurationInstallations = cloneDeep(configurationInstallations);

    const primaryConfigurationInstallation = configurationInstallations.find(ci => !ci.boardNumber);
    const secondaryConfigurationInstallation = configurationInstallations.find(ci => ci.boardNumber);

    // pas un mixage de pose
    if (!primaryConfigurationInstallation || !secondaryConfigurationInstallation) {
      return 1;
    }

    // on met à 1 le boarNumber de la pose primaire pour simulé le fait qu'il peut y avoir au moins une lame de celui-ci
    primaryConfigurationInstallation.boardNumber = 1;

    // et on met à null le boardNumber de la pose secondaire pour obtenir le max de lame possible pour cette pose
    secondaryConfigurationInstallation.boardNumber = null;


    const fencePanel = this.fencePanelCalculatorService.calculate(postHeight, undefined, [
      primaryConfigurationInstallation,
      secondaryConfigurationInstallation,
    ]);

    // on retourne le nombre de lame possible pour la pose secondaire
    return (
      fencePanel.installationColors.find(ic => ic.installation.id === secondaryConfigurationInstallation.installationId)
        ?.colors.length ?? 0
    );
  }

  /**
   * Permet de connaitre le nombre de lames maximum pour la pose secondaire d'un mixage de lame
   * @param currentConfigurationInstallation
   * @param configurationInstallations
   * @param postHeight
   */
  maxBoardFromSecondaryInstallationSecondaryBoard(
    currentConfigurationInstallation: ConfigurationInstallation,
    configurationInstallations: ConfigurationInstallation[],
    postHeight: number
  ): number {
    currentConfigurationInstallation = cloneDeep(currentConfigurationInstallation);
    configurationInstallations = cloneDeep(configurationInstallations);

    const currentFenceSections = this.fencePanelCalculatorService.calculate(
      postHeight,
      undefined,
      configurationInstallations
    );

    const currentPrimaryConfigurationBoard = currentConfigurationInstallation.configurationBoards.find(
      cb => !cb.boardNumber
    );
    const currentSecondaryConfigurationBoard = currentConfigurationInstallation.configurationBoards.find(
      cb => cb.boardNumber
    );

    // pas un mixage de lame
    if (!currentSecondaryConfigurationBoard) {
      return 1;
    }

    // on met à 1 le boarNumber de la pose primaire pour simuler le fait qu'il peut y avoir au moins une lame de celui-ci
    currentPrimaryConfigurationBoard.boardNumber = 1;

    // et on met à null le boardNumber de la pose secondaire pour obtenir le max de lame possible pour cette pose
    currentSecondaryConfigurationBoard.boardNumber = null;

    const otherConfigurationInstallation = configurationInstallations.find(
      ci => ci.installationId !== currentConfigurationInstallation.installationId
    );

    // si ce n'est pas un mixage de pose
    if (!otherConfigurationInstallation) {
      const fencePanel = this.fencePanelCalculatorService.calculate(postHeight, undefined, [
        currentConfigurationInstallation,
      ]);

      // on retourne le nombre de lames possible pour la lame secondaire
      return (
        fencePanel.installationColors
          .find(ic => ic.installation.id === currentConfigurationInstallation.installationId)
          ?.colors.filter(c => c.id === currentSecondaryConfigurationBoard.boardId).length ?? 0
      );
    }

    // si c'est un mixage de pose
    // le boardNumber est égale au nombre de lames actuel possible pour cette installation
    otherConfigurationInstallation.boardNumber ??= currentFenceSections.installationColors.find(i => i.installation.id === otherConfigurationInstallation.installationId)?.colors.length ?? 0;

    // on sauvegarde le boardNumber de la pose courante avant de le mettre à null
    const currentInstallationBoardNumber = currentConfigurationInstallation.boardNumber;

    // on met à null de la pose qu'on veut calculer pour pouvoir récupérer le max possible
    currentConfigurationInstallation.boardNumber = null;

    const fencePanel = this.fencePanelCalculatorService.calculate(postHeight, undefined, [
      currentConfigurationInstallation,
      otherConfigurationInstallation,
    ]);

    // on retourne le nombre de lames possible pour la lame secondaire
    const maxBoard =
      fencePanel.installationColors
        .find(ic => ic.installation.id === currentConfigurationInstallation.installationId)
        ?.colors.filter(c => c.id === currentSecondaryConfigurationBoard.boardId).length ?? 0;

    // le max ne peut pas être supérieur au nombre de lames indiqué dans la configuration de la pose courante - 1
    // (car il faut au moins une lame primaire dans la pose)
    const maxAllowed = currentInstallationBoardNumber != null ? currentInstallationBoardNumber - 1 : maxBoard;
    return Math.min(maxBoard, maxAllowed);
  }
}
